<template>
  <section class="detail">
    <button class="back" @click="$router.push('/dashboard')">← Dashboard</button>

    <LoadingSpinnerComponent v-if="loading && !link" />

    <template v-else-if="link">
      <!-- Header -->
      <div class="head">
        <div class="head-main">
          <template v-if="!editing">
            <div class="head-title-row">
              <h1 class="slug mono">{{ shortDisplay(link.slug) }}</h1>
              <StatusBadge status="active" label="active" />
            </div>
            <div class="head-dest">
              <span class="faint">→</span>
              <span v-if="link.type === 'file'" class="faint">📎</span>
              <router-link
                v-if="link.type === 'file' && link.file_id"
                class="mono ellipsis dest-file-link"
                :to="{ name: 'FileDetails', params: { id: link.file_id } }"
                title="View file details"
              >{{ destinationLabel }}</router-link>
              <span v-else class="mono ellipsis">{{ destinationLabel }}</span>
            </div>
          </template>
          <div v-else class="edit-card lk-card">
            <div>
              <label class="lk-label">SLUG</label>
              <div class="slug-input">
                <span class="mono faint">{{ shortHost }}/</span>
                <input v-model="editSlug" class="mono slug-field" />
              </div>
            </div>
            <div v-if="link.type === 'redirect'">
              <label class="lk-label">DESTINATION URL</label>
              <input v-model="editDest" class="lk-input mono" />
            </div>
            <div v-else>
              <label class="lk-label">DESTINATION FILE</label>
              <select v-model="editFileId" class="lk-input mono">
                <option v-if="!files.length" value="" disabled>No files uploaded yet</option>
                <option v-for="f in files" :key="f.file_id" :value="f.file_id">
                  {{ f.name }}
                </option>
              </select>
            </div>
            <div class="edit-actions">
              <button class="lk-btn lk-btn-primary lk-btn-sm" @click="saveEdit">Save</button>
              <button class="lk-btn lk-btn-sm" @click="editing = false">Cancel</button>
            </div>
          </div>
        </div>
        <div v-if="!editing" class="head-actions">
          <button class="lk-btn" title="Open the short link in a new tab (counts as a click)" @click="openPreview">Open ↗</button>
          <CopyButton :text="shortUrl(link.slug)" />
          <template v-if="link.is_owner">
            <button class="lk-btn" @click="startEdit">Edit</button>
            <button class="lk-btn lk-btn-danger" @click="onDelete">Delete</button>
          </template>
          <button v-else-if="!link.owned && authedNow" class="lk-btn" @click="onClaim">Claim link</button>
          <button v-else-if="!link.owned" class="lk-btn" @click="signInAndReturn(true)">Sign in &amp; claim</button>
        </div>
      </div>

      <!-- Non-owners get an honest "locked" panel instead of empty charts:
           analytics are owner-only and the way in is signing in / claiming. -->
      <div v-if="!link.is_owner" class="grid-main locked-grid">
        <div class="lk-card locked-card">
          <div class="locked-icon">📊</div>
          <div class="locked-title">Analytics are owner-only</div>
          <p class="locked-sub">
            <template v-if="!authedNow && !link.owned">
              This link hasn't been claimed yet. Sign in to claim it — clicks, devices and
              referrers unlock once it's yours, along with editing and deletion.
            </template>
            <template v-else-if="!authedNow">
              Sign in as this link's owner to see its clicks, devices and referrers.
            </template>
            <template v-else-if="!link.owned">
              This link hasn't been claimed. Claim it to see analytics and manage it.
            </template>
            <template v-else>
              You're viewing someone else's link — only its owner can see analytics or edit it.
            </template>
          </p>
          <button v-if="!authedNow" class="lk-btn lk-btn-primary" @click="signInAndReturn(!link.owned)">
            {{ link.owned ? '🔒 Sign in to view' : '🔒 Sign in & claim' }}
          </button>
          <button v-else-if="!link.owned" class="lk-btn lk-btn-primary" @click="onClaim">✓ Claim this link</button>
        </div>
        <div class="side">
          <div class="lk-card qr-card">
            <QrCode ref="qr" :value="shortUrl(link.slug)" :size="78" />
            <div class="qr-meta">
              <div class="qr-title">QR code</div>
              <div class="qr-sub">Download a PNG for print.</div>
              <button class="qr-dl" @click="downloadQr">↓ Download</button>
            </div>
          </div>
        </div>
      </div>

      <template v-else>
      <!-- Stat cards (mock F2 order; clicks in terracotta) -->
      <div class="stat-grid">
        <StatCard label="Total clicks" :value="totalClicks" accent />
        <StatCard label="Last clicked" :value="lastClicked" />
        <StatCard label="Unique visitors" :value="uniqueVisitors" />
        <StatCard label="Destination" :value="destHost" />
      </div>

      <!-- Analytics -->
      <div class="grid-main">
        <div class="lk-card chart-card">
          <div class="card-head">
            <span class="card-title">Clicks — last 14 days</span>
            <span class="faint small">peak {{ peak }}/day</span>
          </div>
          <div class="chart">
            <div v-for="(b, i) in bars" :key="i" class="bar-col" :title="b.val + ' clicks'">
              <div class="bar" :class="b.tier" :style="{ height: b.hpct }"></div>
            </div>
          </div>
          <div v-if="!hasData" class="empty-overlay">Not enough data yet — clicks appear here once your link gets traffic.</div>
        </div>

        <div class="side">
          <div class="lk-card side-card">
            <div class="side-title">Devices</div>
            <template v-if="devices.length">
              <div class="device-bar">
                <div v-for="(d, i) in devices" :key="i" :style="{ width: d.pct + '%', background: d.color }"></div>
              </div>
              <div class="device-list">
                <div v-for="(d, i) in devices" :key="i" class="device-row">
                  <span class="dot" :style="{ background: d.color }"></span>
                  <span class="device-name">{{ d.name }}</span>
                  <span class="mono">{{ d.pct }}%</span>
                </div>
              </div>
            </template>
            <div v-else class="side-empty">No device data yet.</div>
          </div>
          <div class="lk-card side-card">
            <div class="side-title">Top referrers</div>
            <template v-if="topReferrers.length">
              <div v-for="(r, i) in topReferrers" :key="i" class="ref-row">
                <span class="ref-name mono ellipsis">{{ r.label }}</span>
                <span class="mono">{{ r.count }}</span>
              </div>
            </template>
            <div v-else class="side-empty">No referrer data yet.</div>
          </div>
          <div class="lk-card qr-card">
            <QrCode ref="qr" :value="shortUrl(link.slug)" :size="78" />
            <div class="qr-meta">
              <div class="qr-title">QR code</div>
              <div class="qr-sub">Download a PNG for print.</div>
              <button class="qr-dl" @click="downloadQr">↓ Download</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Customization (owner-only): splash + social link preview.
           Mock F2: bordered card, two columns split by a hairline, footer strip. -->
      <div class="cz-card">
        <div class="cz-cardhead">
          <span class="cz-title">Customize</span>
          <span class="cz-chip">drives the micro-site</span>
        </div>

        <div class="cz-cols">
          <!-- Splash screen -->
          <div class="cz-col cz-col-splash">
            <div class="cz-colhead">Splash screen</div>
            <label class="cz-toggle-row">
              <input type="checkbox" v-model="cz.showSplash" class="sw-input" />
              <span class="sw" aria-hidden="true"><span class="sw-knob"></span></span>
              <span>Show splash before redirecting <span class="faint">(off = instant redirect)</span></span>
            </label>
            <div v-if="cz.showSplash" class="cz-knobs">
              <div>
                <div class="cz-label">Theme</div>
                <div class="cz-seg">
                  <button type="button" class="seg-btn" :class="{ on: cz.splashTheme === 'light' }" @click="cz.splashTheme = 'light'">Light</button>
                  <button type="button" class="seg-btn" :class="{ on: cz.splashTheme === 'dark' }" @click="cz.splashTheme = 'dark'">Dark</button>
                </div>
              </div>
              <div>
                <div class="cz-label">Accent</div>
                <div class="cz-swatches">
                  <button
                    type="button"
                    class="swatch swatch-default"
                    :class="{ on: !cz.useAccent }"
                    title="Default terracotta"
                    @click="setAccent(null)"
                  ></button>
                  <button
                    v-for="hex in presetAccents"
                    :key="hex"
                    type="button"
                    class="swatch"
                    :class="{ on: cz.useAccent && cz.accentColor.toLowerCase() === hex }"
                    :style="{ background: hex }"
                    :title="hex"
                    @click="setAccent(hex)"
                  ></button>
                  <label class="custom-hex" :class="{ on: customAccentActive }">
                    <input type="color" v-model="cz.accentColor" class="hex-input" @input="cz.useAccent = true" />
                    custom hex
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Link preview / OG -->
          <div class="cz-col">
            <div class="cz-colhead">Link preview</div>
            <div class="cz-colsub">Open Graph — what iMessage, Slack &amp; social cards show.</div>
            <div class="cz-radios">
              <label class="cz-radio">
                <input type="radio" value="destination" v-model="cz.previewMode" />
                <span class="rdo" aria-hidden="true"></span>
                <span :class="{ dim: cz.previewMode !== 'destination' }"><strong>Destination</strong> — show the destination site's own card</span>
              </label>
              <label class="cz-radio">
                <input type="radio" value="custom" v-model="cz.previewMode" />
                <span class="rdo" aria-hidden="true"></span>
                <span :class="{ dim: cz.previewMode !== 'custom' }"><strong>Custom</strong> — your own title, description &amp; image</span>
              </label>
              <label class="cz-radio">
                <input type="radio" value="masked" v-model="cz.previewMode" />
                <span class="rdo" aria-hidden="true"></span>
                <span :class="{ dim: cz.previewMode !== 'masked' }"><strong>Masked</strong> — generic linker.to card; destination hidden</span>
              </label>
            </div>
            <div v-if="cz.previewMode === 'custom'" class="cz-fields">
              <input v-model="cz.previewTitle" class="cz-field" maxlength="255" placeholder="Preview title" />
              <input v-model="cz.previewDescription" class="cz-field" maxlength="500" placeholder="One-line description" />
              <input v-model="cz.previewImageUrl" class="cz-field mono" placeholder="https://…/card.png" />
            </div>
          </div>
        </div>

        <div class="cz-foot">
          <button class="lk-btn lk-btn-primary" :disabled="!czDirty" @click="saveCustomization">Save customization</button>
          <span v-if="czDirty" class="cz-unsaved">Unsaved changes</span>
        </div>
      </div>
      </template>
    </template>

    <div v-else class="empty">
      <div class="empty-icon">🔗</div>
      <div class="empty-title">Link not found</div>
      <button class="lk-btn lk-btn-primary" @click="$router.push('/dashboard')">Back to dashboard</button>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import apiClient from '@/axios';
import LoadingSpinnerComponent from '@/components/LoadingSpinnerComponent.vue';
import StatCard from '@/components/ui/StatCard.vue';
import StatusBadge from '@/components/ui/StatusBadge.vue';
import CopyButton from '@/components/ui/CopyButton.vue';
import QrCode from '@/components/ui/QrCode.vue';
import { shortUrl, shortDisplay, shortBase, normalizeUrl, isValidSlug } from '@/utils/links';
import { getClaimToken } from '@/utils/claimTokens';
import { formatRelativeTime } from '@/utils';
import { toast } from 'vue3-toastify';
import authReadyLoad from '@/mixins/authReadyLoad';

const CHART_DAYS = 14;

export default {
  name: 'LinkDetailView',
  mixins: [authReadyLoad],
  components: { LoadingSpinnerComponent, StatCard, StatusBadge, CopyButton, QrCode },
  props: { slug: { type: String, required: true } },
  data() {
    return {
      editing: false,
      editSlug: '',
      editDest: '',
      editFileId: '',
      analyticsLoading: false,
      analytics: null,
      analyticsDays: 30,
      claimAttempted: false,
      // Curated accent swatches (mock F2); terracotta = default (no override).
      presetAccents: ['#2a6fdb', '#7c3aed'],
      // Customization form (mirrors the link's micro-site fields).
      cz: {
        showSplash: false,
        splashTheme: 'light',
        useAccent: false,
        accentColor: '#c26a2f',
        previewMode: 'destination',
        previewTitle: '',
        previewDescription: '',
        previewImageUrl: '',
      },
    };
  },
  computed: {
    ...mapGetters('shortLinks', ['stateLink', 'stateIsLoading']),
    ...mapGetters('files', ['stateFiles']),
    ...mapGetters('auth', ['isAuthenticated']),
    loading() { return this.stateIsLoading; },
    link() { return this.stateLink; },
    files() { return this.stateFiles || []; },
    shortHost() { return shortBase().replace(/^https?:\/\//, ''); },
    // All click data comes from the server-side analytics aggregation, which
    // only counts real visits through the short link (LINK_REDIRECT). Viewing
    // this page logs a LINK_READ and is never counted as a click.
    totalClicks() { return this.analytics ? this.analytics.total_clicks : '—'; },
    lastClicked() {
      return this.analytics && this.analytics.last_clicked
        ? formatRelativeTime(this.analytics.last_clicked)
        : '—';
    },
    hasData() { return !!this.analytics && this.analytics.total_clicks > 0; },
    destinationLabel() {
      if (!this.link) return '—';
      if (this.link.type === 'redirect') return this.link.redirect_to || '—';
      const f = this.files.find(x => x.file_id === this.link.file_id);
      return f ? f.name : (this.link.file_id || '—');
    },
    destHost() {
      if (!this.link) return '—';
      if (this.link.type === 'file') return 'File';
      try { return new URL(this.link.redirect_to).hostname.replace(/^www\./, ''); } catch (e) { return '—'; }
    },
    topReferrers() {
      return (this.analytics && this.analytics.top_referrers) || [];
    },
    czDirty() {
      if (!this.link) return false;
      const stored = JSON.stringify({
        show_splash: !!this.link.show_splash,
        splash_theme: this.link.splash_theme || 'light',
        accent_color: this.link.accent_color || null,
        preview_mode: this.link.preview_mode || 'destination',
        preview_title: this.link.preview_title || null,
        preview_description: this.link.preview_description || null,
        preview_image_url: this.link.preview_image_url || null,
      });
      const current = this.customizationPayload();
      ['preview_title', 'preview_description', 'preview_image_url'].forEach(k => {
        if (!current[k]) current[k] = null;
      });
      return stored !== JSON.stringify(current);
    },
    uniqueVisitors() {
      return this.analytics ? this.analytics.unique_visitors : '—';
    },
    customAccentActive() {
      return this.cz.useAccent && !this.presetAccents.includes((this.cz.accentColor || '').toLowerCase());
    },
    bars() {
      // Last CHART_DAYS days of the server timeseries. Bars ramp through the
      // terracotta scale (mock F2): base → mid (≥65% of peak) → peak (max).
      const series = (this.analytics && this.analytics.timeseries) || [];
      const window = series.slice(-CHART_DAYS);
      const counts = window.length ? window.map(p => p.clicks) : new Array(CHART_DAYS).fill(0);
      const max = Math.max(1, ...counts);
      return counts.map(v => ({
        val: v,
        hpct: (v / max * 100) + '%',
        tier: v > 0 && v === max ? 'peak' : (v / max >= 0.65 ? 'mid' : ''),
      }));
    },
    peak() { return Math.max(0, ...this.bars.map(b => b.val)); },
    devices() {
      const top = (this.analytics && this.analytics.top_devices) || [];
      const total = top.reduce((a, d) => a + d.count, 0);
      if (!total) return [];
      const colors = {
        Desktop: 'var(--accent)', Mobile: 'var(--text)',
        Tablet: 'var(--faint)', Bot: 'var(--border-strong)',
      };
      return top.map(d => ({
        name: d.label,
        pct: Math.round(d.count / total * 100),
        color: colors[d.label] || 'var(--faint)',
      }));
    },
  },
  watch: {
    slug: { immediate: true, handler() { this.load(); } },
    link: {
      immediate: true,
      handler(l) {
        if (l) {
          this.resetCustomization(l);
          this.maybeAutoClaim();
        }
      },
    },
  },
  methods: {
    ...mapActions('shortLinks', ['getLink', 'updateLink', 'deleteLink', 'claimLink']),
    ...mapActions('files', ['getFiles']),
    shortUrl,
    shortDisplay,
    // Slug watcher (immediate + on change): the link itself is public, so load
    // it always. Analytics are owner-only — fetchAnalytics no-ops until auth is
    // ready and the requester owns the link.
    async load() {
      await this.getLink(this.slug);
      this.fetchAnalytics();
      // File names for file-link destinations come from the user's file list.
      if (this.link && this.link.type === 'file' && this.authedNow && !this.stateFiles) {
        this.getFiles().catch(() => {});
      }
    },
    // Called by the authReadyLoad mixin once the session restore has settled and
    // the user is authenticated (covers the cold-load race). Deduped against the
    // slug watcher via `analyticsLoading`.
    loadAuthedData() {
      this.fetchAnalytics();
      this.maybeAutoClaim();
      if (this.link && this.link.type === 'file' && !this.stateFiles) {
        this.getFiles().catch(() => {});
      }
    },
    // Send an anonymous visitor through login and straight back here.
    // withClaim adds ?claim=1 so the return trip finishes the claim
    // automatically (see maybeAutoClaim).
    signInAndReturn(withClaim) {
      const redirect = `/dashboard/link/${this.slug}${withClaim ? '?claim=1' : ''}`;
      this.$router.push({ name: 'Login', query: { redirect } });
    },
    // Arriving with ?claim=1 (post-login) completes the claim without another
    // click, provided this browser still holds the link's claim token. Runs
    // from both the link watcher and loadAuthedData — whichever settles last
    // has everything it needs; claimAttempted keeps it single-shot.
    async maybeAutoClaim() {
      if (this.$route.query.claim !== '1' || this.claimAttempted) return;
      if (!this.authReady || !this.authedNow) return;
      if (!this.link || this.link.owned) return;
      if (!getClaimToken('link', this.link.slug)) return;
      this.claimAttempted = true;
      // Drop the flag from the URL so a refresh doesn't re-claim.
      const query = { ...this.$route.query };
      delete query.claim;
      this.$router.replace({ query });
      await this.onClaim();
    },
    async fetchAnalytics() {
      // Owner-only endpoint; skip for anonymous viewers and non-owners.
      if (!this.authReady || !this.authedNow) { this.analytics = null; return; }
      if (this.link && this.link.is_owner === false) { this.analytics = null; return; }
      if (this.analyticsLoading) return;
      this.analyticsLoading = true;
      try {
        const res = await apiClient.get(`links/${this.slug}/analytics`, {
          params: { days: this.analyticsDays },
        });
        this.analytics = res.data;
      } catch (e) {
        this.analytics = null;
      } finally {
        this.analyticsLoading = false;
      }
    },
    // Open the real short link (splash and all) in a new tab. Deliberately no
    // "don't count me" flag: a flagged URL would inevitably get copy-pasted
    // into shares and silently zero those clicks. An owner's own click
    // counting once is the cheaper error.
    openPreview() {
      window.open(shortUrl(this.link.slug), '_blank', 'noopener');
    },
    downloadQr() {
      if (this.$refs.qr) this.$refs.qr.download(`linker-${this.link.slug}-qr.png`);
    },
    // Swatch click: null = default terracotta (accent_color stays null in the
    // payload); a hex forces that accent on the micro-site.
    setAccent(hex) {
      if (!hex) {
        this.cz.useAccent = false;
      } else {
        this.cz.useAccent = true;
        this.cz.accentColor = hex;
      }
    },
    startEdit() {
      this.editSlug = this.link.slug;
      this.editDest = this.link.redirect_to || '';
      this.editFileId = this.link.file_id || '';
      this.editing = true;
      if (this.link.type === 'file' && !this.stateFiles) {
        this.getFiles().catch(() => {});
      }
    },
    async saveEdit() {
      const newSlug = (this.editSlug || '').trim();
      if (!isValidSlug(newSlug)) {
        toast.error('Slug may only contain letters, numbers, - and _ (max 64)');
        return;
      }
      const payload = { slug: this.link.slug, newSlug, type: this.link.type };
      if (this.link.type === 'redirect') {
        const norm = normalizeUrl(this.editDest);
        if (!norm.ok) { toast.error(norm.error); return; }
        payload.redirectTo = norm.url;
      } else {
        if (!this.editFileId) { toast.error('Pick a file for this link'); return; }
        payload.fileId = this.editFileId;
      }
      try {
        const data = await this.updateLink(payload);
        this.editing = false;
        if (data && data.slug && data.slug !== this.slug) {
          this.$router.replace(`/dashboard/link/${data.slug}`);
        }
      } catch (e) { /* store toasts */ }
    },
    resetCustomization(l) {
      this.cz = {
        showSplash: !!l.show_splash,
        splashTheme: l.splash_theme || 'light',
        useAccent: !!l.accent_color,
        accentColor: l.accent_color || '#c26a2f',
        previewMode: l.preview_mode || 'destination',
        previewTitle: l.preview_title || '',
        previewDescription: l.preview_description || '',
        previewImageUrl: l.preview_image_url || '',
      };
    },
    customizationPayload() {
      // The preview_* overrides are kept even when the mode isn't 'custom' —
      // switching modes back and forth shouldn't wipe them. Only preview_mode
      // decides what the micro-site renders.
      return {
        show_splash: this.cz.showSplash,
        splash_theme: this.cz.splashTheme,
        accent_color: this.cz.useAccent ? this.cz.accentColor : null,
        preview_mode: this.cz.previewMode,
        preview_title: (this.cz.previewTitle || '').trim() || null,
        preview_description: (this.cz.previewDescription || '').trim() || null,
        preview_image_url: (this.cz.previewImageUrl || '').trim() || null,
      };
    },
    async saveCustomization() {
      const extras = this.customizationPayload();
      if (extras.preview_image_url) {
        const norm = normalizeUrl(extras.preview_image_url);
        if (!norm.ok) { toast.error('Preview image: ' + norm.error); return; }
        extras.preview_image_url = norm.url;
      }
      const payload = { slug: this.link.slug, type: this.link.type, extras };
      if (this.link.type === 'file') payload.fileId = this.link.file_id;
      else payload.redirectTo = this.link.redirect_to;
      try {
        await this.updateLink(payload);
      } catch (e) { /* store toasts */ }
    },
    async onClaim() {
      try {
        await this.claimLink(this.link.slug);
        await this.getLink(this.slug);
        this.fetchAnalytics();
      } catch (e) { /* store toasts */ }
    },
    async onDelete() {
      try {
        await this.deleteLink(this.link.slug);
        this.$router.push('/dashboard');
      } catch (e) { /* store toasts */ }
    },
  },
};
</script>

<style scoped>
.detail {
  max-width: 960px;
  margin: 0 auto;
  padding: clamp(20px, 3vw, 36px) clamp(16px, 4vw, 40px) 80px;
}
.back {
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 18px;
}
.back:hover { color: var(--text); }

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 26px;
  flex-wrap: wrap;
}
.head-main { min-width: 0; }
.head-title-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; flex-wrap: wrap; }
.slug { font-size: 27px; font-weight: 500; letter-spacing: -.02em; margin: 0; color: var(--accent); }
.head-dest { font-size: 13.5px; color: var(--muted); display: flex; align-items: center; gap: 7px; }
.dest-file-link { color: var(--accent); text-decoration: none; }
.dest-file-link:hover { text-decoration: underline; }
.head-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.locked-grid { margin-top: 4px; }
.locked-card {
  padding: 36px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
}
.locked-icon { font-size: 30px; opacity: .7; }
.locked-title { font-size: 16px; font-weight: 700; }
.locked-sub { font-size: 13px; color: var(--muted); max-width: 420px; margin: 0 0 12px; line-height: 1.5; }

.edit-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; min-width: 280px; }
.edit-card select.lk-input { width: 100%; }
.slug-input {
  display: flex;
  align-items: center;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  border-radius: var(--radius);
  padding: 0 12px;
}
.slug-field { flex: 1; border: none; background: transparent; color: var(--text); font-size: 13.5px; padding: 10px 0; }
.edit-actions { display: flex; gap: 8px; }

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.grid-main {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  align-items: start;
}
.chart-card { padding: 20px; position: relative; }
.card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.card-title { font-family: var(--font-display); font-weight: 400; font-size: 18px; letter-spacing: -.01em; }
.small-title { font-size: 17px; margin-bottom: 14px; display: block; }
.small { font-size: 12px; }
.faint { color: var(--faint); }
.chart { display: flex; align-items: flex-end; gap: 5px; height: 150px; }
.bar-col { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; height: 100%; }
/* Terracotta ramp (mock F2): base #e3cbb2 → mid #d99a5f → peak #c26a2f,
   derived from the accent so custom themes stay coherent. */
.bar {
  width: 100%;
  background: color-mix(in srgb, var(--accent) 30%, var(--card-alt));
  border-radius: 2px 2px 0 0;
  transform-origin: bottom;
  animation: lk-bar .5s ease both;
  min-height: 3px;
}
.bar.mid { background: color-mix(in srgb, var(--accent) 62%, var(--card-alt)); }
.bar.peak { background: var(--accent); }
.empty-overlay {
  position: absolute;
  inset: 52px 20px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12.5px;
  color: var(--muted);
  background: color-mix(in srgb, var(--card) 78%, transparent);
  border-radius: var(--radius);
  padding: 20px;
}

.side { display: flex; flex-direction: column; gap: 14px; }
.side-card { padding: 18px; }
.side-title { font-size: 13.5px; font-weight: 700; margin-bottom: 14px; }
.device-bar { display: flex; height: 9px; border-radius: 3px; overflow: hidden; margin-bottom: 14px; }
.device-list { display: flex; flex-direction: column; gap: 9px; }
.device-row { display: flex; align-items: center; gap: 8px; font-size: 12.5px; }
.device-row .dot { width: 9px; height: 9px; border-radius: 2px; }
.device-name { flex: 1; color: var(--muted); }
.side-empty { font-size: 12.5px; color: var(--muted); padding: 8px 0; }

/* QR card (mock F2): horizontal — thumb + copy + terracotta download */
.qr-card { padding: 16px; display: flex; align-items: center; gap: 14px; }
.qr-meta { min-width: 0; }
.qr-title { font-size: 13px; font-weight: 700; color: var(--text); }
.qr-sub { font-size: 12px; color: var(--faint); margin-top: 3px; }
.qr-dl {
  border: none;
  background: transparent;
  color: var(--accent);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  display: block;
}
.qr-dl:hover { color: var(--accent-h); }

.ref-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 12.5px; padding: 6px 0; border-bottom: 1px solid var(--border); }
.ref-row:last-child { border-bottom: none; }
.ref-name { color: var(--muted); min-width: 0; }

/* ===== Customize panel (mock F2) ===== */
.cz-card {
  margin-top: 16px;
  background: var(--card-alt);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.cz-cardhead {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 22px;
  border-bottom: 1px solid var(--border);
  flex-wrap: wrap;
}
.cz-title { font-size: 15px; font-weight: 700; }
.cz-chip {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--accent-h);
  background: var(--accent-soft);
  border: 1px solid var(--border-strong);
  border-radius: 4px;
  padding: 2px 7px;
}
.cz-cols { display: grid; grid-template-columns: 1fr 1fr; }
.cz-col { padding: 22px; min-width: 0; }
.cz-col-splash { border-right: 1px solid var(--border); }
.cz-colhead { font-size: 12.5px; font-weight: 700; margin-bottom: 4px; color: var(--text); }
.cz-col-splash .cz-colhead { margin-bottom: 14px; }
.cz-colsub { font-size: 11.5px; color: var(--faint); margin-bottom: 14px; }

/* Toggle switch (36×20 pill, terracotta when on) */
.cz-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 16px;
  cursor: pointer;
}
.sw-input { position: absolute; opacity: 0; width: 0; height: 0; }
.sw {
  width: 36px;
  height: 20px;
  border-radius: 100px;
  background: var(--border-strong);
  position: relative;
  flex-shrink: 0;
  transition: background .18s;
}
.sw-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform .18s;
}
.sw-input:checked + .sw { background: var(--accent); }
.sw-input:checked + .sw .sw-knob { transform: translateX(16px); }
.sw-input:focus-visible + .sw { outline: 2px solid var(--accent); outline-offset: 2px; }

.cz-knobs {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 14px;
  align-items: start;
}
.cz-label {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--faint);
  margin-bottom: 7px;
}

/* Theme segmented control */
.cz-seg { display: flex; gap: 6px; }
.seg-btn {
  font-size: 12px;
  font-weight: 500;
  color: var(--muted);
  background: var(--field);
  border: 1px solid var(--border-strong);
  border-radius: 5px;
  padding: 7px 14px;
  cursor: pointer;
  transition: background .15s, color .15s;
}
.seg-btn.on {
  font-weight: 600;
  color: var(--paper);
  background: var(--ink);
  border-color: var(--ink);
}

/* Accent swatches */
.cz-swatches { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; }
.swatch {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: box-shadow .15s;
}
.swatch-default { background: #c26a2f; border-color: #a8531f; }
.swatch.on { box-shadow: 0 0 0 2px var(--card-alt), 0 0 0 3.5px var(--text); }
.custom-hex {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: var(--faint);
  cursor: pointer;
}
.custom-hex.on { color: var(--text); font-weight: 600; }
.hex-input {
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  background: var(--field);
  cursor: pointer;
}

/* Preview radios */
.cz-radios { display: flex; flex-direction: column; gap: 9px; margin-bottom: 14px; }
.cz-radio {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
}
.cz-radio input { position: absolute; opacity: 0; width: 0; height: 0; }
.rdo {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 4px solid var(--border-strong);
  flex-shrink: 0;
  margin-top: 1px;
  transition: border-color .15s;
  background: var(--field);
}
.cz-radio input:checked + .rdo { border-color: var(--accent); }
.cz-radio input:focus-visible + .rdo { outline: 2px solid var(--accent); outline-offset: 2px; }
.cz-radio .dim { color: var(--muted); }
.cz-radio .dim strong { color: var(--faint); font-weight: 600; }

.cz-fields { display: flex; flex-direction: column; gap: 8px; padding-left: 24px; }
.cz-field {
  background: var(--field);
  border: 1px solid var(--border-strong);
  border-radius: 5px;
  padding: 9px 12px;
  font-size: 12.5px;
  color: var(--text);
  width: 100%;
  transition: border-color .15s, box-shadow .15s;
}
.cz-field:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring); }
.cz-field::placeholder { color: var(--faint); }

.cz-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 22px;
  border-top: 1px solid var(--border);
  background: var(--card);
}
.cz-unsaved { font-size: 12px; color: var(--accent-h); }

.mono { font-family: var(--font-mono); }
.ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.empty { text-align: center; padding: 60px 24px; }
.empty-icon { font-size: 32px; margin-bottom: 10px; opacity: .6; }
.empty-title { font-size: 15px; font-weight: 600; margin-bottom: 16px; }

/* Mobile (mock F2m): everything single-column, stats 2-up, shorter chart */
@media (max-width: 760px) {
  .grid-main { grid-template-columns: 1fr; }
  .stat-grid { grid-template-columns: 1fr 1fr; }
  .chart { height: 90px; }
  .cz-cols { grid-template-columns: 1fr; }
  .cz-col-splash { border-right: none; border-bottom: 1px solid var(--border); }
  .cz-knobs { grid-template-columns: 1fr; }
}
</style>
